/**
 * WikiApiClient — `GET /api/v1/wiki/layers` with one memoised promise, plus
 * the king-admin text-editing calls (0569).
 *
 * The catalogue is small and changes only on import/build, so the first
 * successful response is shared by every caller for the page's lifetime
 * (every view of /docs renders from the same promise). A failed request is
 * NOT cached: the next call fetches again, which is what "Poskusi znova"
 * relies on. Cookie auth (credentials: 'include'); the route allows
 * anonymous readers — the public docs page is anonymous.
 *
 * `fetchMe()` never throws: an anonymous visitor (401) is simply not an
 * admin, and the page must render identically whether the probe works or
 * not. `fetchApps()` (#875, the header's way back to the map) never throws
 * either: a failed call is an empty list. The admin calls throw WikiApiError with the status and the server's
 * machine `code` — never its message text (client CLAUDE.md).
 */

import type {
  AdminWikiEntryResponse,
  WikiHistoryResponse,
  WikiLayersResponse,
  WikiRestoreBody,
  WikiTextPatch,
} from '../../shared/types/layerWiki.js';
import type { DocsApp } from './docsBackLinks.js';

export interface WikiApi {
  fetchWikiLayers(): Promise<WikiLayersResponse>;
  invalidate(): void;
  /** Is the viewer a king admin? Anonymous, refused or failed → false; memoised. */
  fetchMe(): Promise<{ isKingAdmin: boolean }>;
  /** The viewer's apps (anonymous: the public ones) for the header's way back (#875); failed → []; memoised. */
  fetchApps?(): Promise<DocsApp[]>;
  patchEntryText(entryKey: string, patch: WikiTextPatch): Promise<AdminWikiEntryResponse>;
  fetchHistory(entryKey: string): Promise<WikiHistoryResponse>;
  restoreEntryText(entryKey: string, body: WikiRestoreBody): Promise<AdminWikiEntryResponse>;
}

/** A non-2xx from an admin call: the status and the server's machine code, nothing else. */
export class WikiApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    what: string
  ) {
    super(`${what} failed (${status}${code ? ` ${code}` : ''})`);
    this.name = 'WikiApiError';
  }
}

function checkOk(res: Response, what: string): Response {
  if (!res.ok) {
    // The server `error` string is never rendered (client CLAUDE.md) — only the
    // status travels, for the console.
    throw new Error(`${what} failed (${res.status})`);
  }
  return res;
}

async function readCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as { code?: unknown };
    return typeof body.code === 'string' ? body.code : null;
  } catch {
    return null;
  }
}

async function adminJson<T>(url: string, init: RequestInit, what: string): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!res.ok) throw new WikiApiError(res.status, await readCode(res), what);
  return (await res.json()) as T;
}

function entryUrl(entryKey: string, suffix: string): string {
  return `/api/v1/admin/wiki/layers/${encodeURIComponent(entryKey)}/${suffix}`;
}

export class WikiApiClient implements WikiApi {
  private inflight: Promise<WikiLayersResponse> | null = null;
  private me: Promise<{ isKingAdmin: boolean }> | null = null;
  private apps: Promise<DocsApp[]> | null = null;

  fetchWikiLayers(): Promise<WikiLayersResponse> {
    if (this.inflight) return this.inflight;
    const p: Promise<WikiLayersResponse> = this.load().catch((err: unknown) => {
      // Only forget OUR promise — an invalidate()+refetch in between must keep its own.
      if (this.inflight === p) this.inflight = null;
      throw err;
    });
    this.inflight = p;
    return p;
  }

  /** Drop the memoised result so the next call hits the server again. */
  invalidate(): void {
    this.inflight = null;
  }

  fetchMe(): Promise<{ isKingAdmin: boolean }> {
    if (!this.me) this.me = this.probeMe();
    return this.me;
  }

  fetchApps(): Promise<DocsApp[]> {
    if (!this.apps) this.apps = this.loadApps();
    return this.apps;
  }

  patchEntryText(entryKey: string, patch: WikiTextPatch): Promise<AdminWikiEntryResponse> {
    return adminJson(entryUrl(entryKey, 'text'), { method: 'PATCH', body: JSON.stringify(patch) }, 'Edit wiki text');
  }

  fetchHistory(entryKey: string): Promise<WikiHistoryResponse> {
    return adminJson(entryUrl(entryKey, 'history'), { method: 'GET' }, 'Load wiki text history');
  }

  restoreEntryText(entryKey: string, body: WikiRestoreBody): Promise<AdminWikiEntryResponse> {
    return adminJson(entryUrl(entryKey, 'restore'), { method: 'POST', body: JSON.stringify(body) }, 'Restore wiki text');
  }

  private async load(): Promise<WikiLayersResponse> {
    const res = checkOk(await fetch('/api/v1/wiki/layers', { credentials: 'include' }), 'Load layer wiki');
    return (await res.json()) as WikiLayersResponse;
  }

  private async loadApps(): Promise<DocsApp[]> {
    try {
      const res = await fetch('/api/v1/me/sxapps', { credentials: 'include' });
      if (!res.ok) return [];
      const body = (await res.json()) as { apps?: Array<{ appId?: unknown; capabilities?: unknown }> };
      return (body.apps ?? [])
        .filter((a): a is { appId: string; capabilities?: unknown } => typeof a.appId === 'string')
        .map((a) => ({
          appId: a.appId,
          capabilities: Array.isArray(a.capabilities) ? a.capabilities.filter((c): c is string => typeof c === 'string') : [],
        }));
    } catch {
      return [];
    }
  }

  private async probeMe(): Promise<{ isKingAdmin: boolean }> {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (!res.ok) return { isKingAdmin: false };
      const body = (await res.json()) as { is_king_admin?: unknown };
      return { isKingAdmin: body.is_king_admin === true };
    } catch {
      return { isKingAdmin: false };
    }
  }
}

/** Module singleton — one catalogue per page. Tests pass their own via `DocsPageOptions.api`. */
export const wikiApi = new WikiApiClient();
